// src/DetalleProducto.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MOCK_PRODUCTOS } from './data/productos';
import { Producto } from './types';
import ShareButton from './ShareButton';
import { getWhatsappLink } from './config';

interface DetalleProductoProps {
    onAddToCart: (producto: Producto) => void;
}

interface LightboxProps {
    imageUrl: string;
    onClose: () => void;
    onNext: () => void;
    onPrev: () => void;
    totalImages: number;
}
const LightboxModal: React.FC<LightboxProps> = ({ imageUrl, onClose, onNext, onPrev, totalImages }) => {
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    const disableNav = totalImages <= 1;

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-95 z-[60] flex flex-col justify-center items-center p-4 backdrop-blur-sm" 
            onClick={onClose}
        >
            <div className="relative w-full max-w-6xl h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-20 bg-black/50 rounded-full p-2">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
                <button onClick={onPrev} disabled={disableNav} className={`absolute left-4 z-10 p-3 bg-black/50 rounded-full text-white hover:bg-yellow-600 transition ${disableNav ? 'opacity-0' : ''}`}>
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                </button>
                <img src={imageUrl} alt="Galería Fullscreen" className="max-w-full max-h-[90vh] object-contain rounded shadow-2xl" />
                <button onClick={onNext} disabled={disableNav} className={`absolute right-4 z-10 p-3 bg-black/50 rounded-full text-white hover:bg-yellow-600 transition ${disableNav ? 'opacity-0' : ''}`}>
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                </button>
            </div>
        </div>
    );
};

const SpecRow: React.FC<{ label: string; value: string | number | undefined }> = ({ label, value }) => (
    <div className="flex justify-between items-center border-b border-gray-100 pb-3 last:border-0 last:pb-0">
        <span className="text-gray-500 font-medium">{label}</span>
        <span className="text-gray-900 font-bold">{value || 'N/A'}</span>
    </div>
);
const FeatureBox: React.FC<{ icon: string; label: string; value: string | number | undefined }> = ({ icon, label, value }) => (
    <div className="bg-gray-50 rounded-lg p-3 text-center">
        <div className="text-2xl mb-1">{icon}</div>
        <p className="text-xs text-gray-500 uppercase font-bold">{label}</p>
        <p className="text-sm font-bold text-gray-900 truncate">{value || 'N/A'}</p>
    </div>
);

// === COMPONENTE PRINCIPAL ===
const DetalleProducto: React.FC<DetalleProductoProps> = ({ onAddToCart }) => {
    
    const { id } = useParams(); 
    const navigate = useNavigate();
    const producto = MOCK_PRODUCTOS.find(p => p.id === Number(id));

    const [mainImage, setMainImage] = useState('');
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    useEffect(() => {
        if (producto) {
            setMainImage(producto.imgUrl);
        }
    }, [producto]);

    if (!producto) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center">
                <h2 className="text-2xl font-bold mb-4">Producto no encontrado</h2>
                <button onClick={() => navigate('/catalogo')} className="text-yellow-600 underline">Volver al catálogo</button>
            </div>
        );
    }

    const allImages = [producto.imgUrl, ...producto.imagenes];
    const openLightbox = (index: number) => setLightboxIndex(index);
    const closeLightbox = () => setLightboxIndex(null);
    const handleNext = () => setLightboxIndex((prev) => prev !== null ? (prev + 1) % allImages.length : null);
    const handlePrev = () => setLightboxIndex((prev) => prev !== null ? (prev - 1 + allImages.length) % allImages.length : null);

    const isMoto = producto.tipo === 'moto';
    const accentColorText = isMoto ? 'text-red-600' : 'text-yellow-600';
    const accentColorBg = isMoto ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700';
    const accentBorder = isMoto ? 'border-red-600' : 'border-yellow-600';

    return (
        <div className="min-h-screen bg-gray-50 pt-24 pb-16 font-sans">
            
            {lightboxIndex !== null && (
                <LightboxModal imageUrl={allImages[lightboxIndex]} onClose={closeLightbox} onNext={handleNext} onPrev={handlePrev} totalImages={allImages.length}/>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                <nav className="flex items-center text-sm text-gray-500 mb-6">
                    <Link to="/catalogo" className="hover:text-black transition flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
                        Volver al Catálogo
                    </Link>
                    <span className="mx-2">/</span>
                    <span className="uppercase font-semibold text-gray-800">{producto.tipo}</span>
                    <span className="mx-2">/</span>
                    <span className={`${accentColorText} font-bold`}>{producto.nombre}</span>
                </nav>

                {/* ENCABEZADO */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-end mb-8">
                    <div className="flex-1">
                        <div className="flex items-center gap-4">
                            <h1 className="text-4xl font-extrabold text-gray-900 leading-tight">
                                {producto.nombre}
                            </h1>
                            <ShareButton producto={producto} className="w-10 h-10 shadow-sm border border-gray-200" />
                        </div>
                        <p className="text-gray-500 mt-2 text-lg">
                            {producto.tipoCarro} • {producto.año || '2024'} • {producto.condicion === 'usado' ? 'Usado Certificado' : 'Nuevo de Agencia'}
                        </p>
                    </div>
                    <div className="mt-4 md:mt-0">
                        <p className={`text-4xl font-bold ${accentColorText}`}>
                            ${producto.precio.toLocaleString('es-ES')}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    {/* COLUMNA IZQUIERDA (GALERÍA Y DETALLES) */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* ... Galería ... */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="relative group cursor-pointer overflow-hidden bg-gray-100 h-[400px] md:h-[500px]" onClick={() => openLightbox(0)}>
                                <img src={mainImage} alt={producto.nombre} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <span className="bg-white/90 text-black px-4 py-2 rounded-full text-sm font-bold shadow-lg flex items-center">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                                        Ver Pantalla Completa
                                    </span>
                                </div>
                            </div>
                            <div className="p-4 flex space-x-3 overflow-x-auto border-t border-gray-100">
                                {allImages.map((img, index) => (
                                    <div key={index} onClick={() => setMainImage(img)} className={`relative w-24 h-20 flex-shrink-0 cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${mainImage === img ? `${accentBorder} opacity-100` : 'border-transparent opacity-60 hover:opacity-100'}`}>
                                        <img src={img} alt={`Thumb ${index}`} className="w-full h-full object-cover" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                <span className={`w-1 h-6 ${isMoto ? 'bg-red-600' : 'bg-yellow-600'} mr-3 rounded-full`}></span>
                                Descripción del Vehículo
                            </h3>
                            <div className="prose max-w-none text-gray-600 leading-relaxed">
                                <p>{producto.descripcion}</p>
                            </div>
                        </div>

                        {(producto.tipo === 'carro' || producto.tipo === 'moto') && (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                                    <span className={`w-1 h-6 ${isMoto ? 'bg-red-600' : 'bg-yellow-600'} mr-3 rounded-full`}></span>
                                    Ficha Técnica y Características
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12">
                                    <SpecRow label="Marca" value={producto.nombre.split(' ')[0]} />
                                    <SpecRow label="Modelo" value={producto.nombre} />
                                    <SpecRow label="Año" value={producto.año} />
                                    {/* Mostrar KM solo si existe */}
                                    {producto.kilometraje && <SpecRow label="Kilometraje" value={producto.kilometraje} />}
                                    <SpecRow label="Tipo de Carrocería" value={producto.tipoCarro} />
                                    <SpecRow label="Motor" value={producto.motor || producto.cilindraje + ' cc'} />
                                    <SpecRow label="Transmisión" value={producto.transmision} />
                                    <SpecRow label="Potencia" value={producto.potencia ? producto.potencia + ' HP' : 'N/A'} />
                                    <SpecRow label="Color" value={producto.color} />
                                    <SpecRow label="Rendimiento Aprox." value={producto.rendimiento ? producto.rendimiento + ' km/L' : 'N/A'} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* COLUMNA DERECHA (SIDEBAR STICKY) */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-28 space-y-6">
                            
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <div className="text-center mb-6 pb-6 border-b border-gray-100">
                                    <p className="text-gray-500 text-sm mb-1">Precio de Contado</p>
                                    <p className={`text-4xl font-extrabold ${accentColorText}`}>
                                        ${producto.precio.toLocaleString('es-ES')}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-8">
                                    <FeatureBox icon="📅" label="Año" value={producto.año} />
                                    {/* Muestra KM o Transmisión dependiendo del espacio */}
                                    <FeatureBox icon="⚙️" label="Transmisión" value={producto.transmision?.split(' ')[0]} />
                                    <FeatureBox icon="⛽" label="Motor" value={producto.motor?.split(' ')[0] || producto.cilindraje} />
                                    <FeatureBox icon="🎨" label="Color" value={producto.color} />
                                </div>

                                <div className="space-y-3">
                                    {(producto.tipo === 'carro' || producto.tipo === 'moto') ? (
                                        <>
                                            <button
                                                onClick={() => window.open(getWhatsappLink(`Hola, estoy interesado en el ${producto.nombre} y quisiera agendar una cita/test drive.`), '_blank')}
                                                className={`w-full ${accentColorBg} text-white font-bold py-4 px-4 rounded-xl transition duration-200 shadow-md flex items-center justify-center`}
                                            >
                                                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                                Agendar Test Drive
                                            </button>
                                            
                                            <button
                                                onClick={() => window.open(getWhatsappLink(`Hola, tengo una consulta sobre el precio y financiamiento del ${producto.nombre}.`), '_blank')}
                                                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-4 rounded-xl transition duration-200 shadow-md flex items-center justify-center"
                                            >
                                                <img src="/WhatsappLogo.webp" alt="WhatsApp" className="w-6 h-6 mr-2" />
                                                Cotizar por WhatsApp
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => onAddToCart(producto)}
                                            className="w-full bg-yellow-600 text-white font-bold py-4 px-4 rounded-xl hover:bg-yellow-700 transition duration-200 shadow-lg flex items-center justify-center transform active:scale-95"
                                        >
                                            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                                            Agregar al Carrito
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="bg-black text-white rounded-2xl shadow-lg p-6 flex flex-col items-center text-center">
                                <img src="/LogoBestune.webp" alt="Bestune Logo" className="h-12 w-auto mb-4" />
                                <h4 className="font-bold text-lg">Bestune Cars Venezuela</h4>
                                <p className="text-gray-400 text-sm mb-4">Distribuidor Autorizado</p>
                                <div className="text-sm text-gray-300 space-y-2">
                                    <p>📍 Sede Principal: Caracas</p>   
                                    <p>📞 +58 4242484359</p>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetalleProducto;